import { z } from 'zod';
import {
  IMAGE_ASPECT_RATIOS,
  IMAGE_BACKGROUNDS,
  IMAGE_QUALITIES,
  IMAGE_STYLE_PRESETS,
} from '@/lib/validators/ai-image.schema';

export const getSettingInput = z.object({
  key: z.string().trim().min(1).max(100),
});

export const listByPrefixInput = z.object({
  prefix: z.string().trim().min(1).max(100),
});

export const brandingConfigSchema = z.object({
  primaryColor: z.string().trim().regex(/^#[0-9a-fA-F]{6}$/),
  secondaryColor: z.string().trim().regex(/^#[0-9a-fA-F]{6}$/),
  tertiaryColor: z.string().trim().regex(/^#[0-9a-fA-F]{6}$/),
  accentColor: z.string().trim().regex(/^#[0-9a-fA-F]{6}$/),
  logoUrl: z.string().trim().url().max(500).optional(),
});

export const upsertBrandingInput = z.object({
  config: brandingConfigSchema,
});

export const exerciseDefaultsSchema = z.object({
  showExplanations: z.boolean(),
  caseSensitiveAnswers: z.boolean(),
});

export const upsertExerciseDefaultsInput = z.object({
  config: exerciseDefaultsSchema,
});

export const homepageWidgetSchema = z.object({
  id: z.string().trim().min(1).max(50),
  label: z.string().trim().min(1).max(100),
  visible: z.boolean(),
  sortOrder: z.number().int().min(0).max(100),
});

export const homepageConfigSchema = z.object({
  widgets: z.array(homepageWidgetSchema).max(20),
});

export const upsertHomepageConfigInput = z.object({
  config: homepageConfigSchema,
});

// Reading Zone quiz tuning — controls question-bank density (words per
// question) and how the per-session quiz length scales with reading effort.
export const readingZoneQuizConfigSchema = z
  .object({
    wordsPerQuestion: z.number().int().min(100).max(2000),
    maxQuestions: z.number().int().min(1).max(20),
    skimSecsPerWindow: z.number().int().min(1).max(600),
    deepMinSecs: z.number().int().min(1).max(3600),
    deepMaxSecs: z.number().int().min(1).max(7200),
  })
  .refine((c) => c.deepMinSecs <= c.deepMaxSecs, {
    message: 'Deep-reading minimum must be ≤ maximum',
    path: ['deepMaxSecs'],
  });

export const upsertReadingZoneQuizConfigInput = z.object({
  config: readingZoneQuizConfigSchema,
});

// Site-wide toggle: when enabled, the Reading Zone is open to all students
// regardless of enrollment.
export const upsertReadingZoneOpenAccessInput = z.object({
  enabled: z.boolean(),
});

// Live Classroom master off-switch. Default OFF; turning it ON also stamps the
// `classroom.reenabled_at` watermark so the unheld-session sweep cannot replay
// the off period.
export const classroomEnabledSchema = z.object({
  enabled: z.boolean(),
});
export type ClassroomEnabledSetting = z.infer<typeof classroomEnabledSchema>;

export const upsertClassroomEnabledInput = z.object({
  enabled: z.boolean(),
});

// Reading Zone grade-gating — books to master at the current top grade before
// the next grade unlocks (capped at the number actually available at that grade).
export const upsertGradeUnlockCountInput = z.object({
  count: z.number().int().min(1).max(1000),
});

// Admin-configurable MAX UPLOAD SIZES, in MEGABYTES. Seven are per file-type
// (material library, assignment submissions, lesson media, thumbnails, covers,
// credentials, grading annotations, reading-aloud all cap by type); five are
// per authoring flow that historically carried its own literal. 1 MB ceiling of
// 2 GB is a sanity bound — the real per-request bound is the stored value.
export const UPLOAD_LIMIT_CEILING_MB = 2048;
export const UPLOAD_LIMIT_CEILING_BYTES = UPLOAD_LIMIT_CEILING_MB * 1024 * 1024;

const uploadMbField = z.number().int().min(1).max(UPLOAD_LIMIT_CEILING_MB);
export const uploadLimitsMbSchema = z.object({
  // Per file-type (S3_CONFIG.maxFileSize buckets).
  image: uploadMbField,
  video: uploadMbField,
  document: uploadMbField,
  audio: uploadMbField,
  presentation: uploadMbField,
  excalidrawTemplate: uploadMbField,
  // 🔴 EIFO report #207 (issue #798) — this key was missing, and
  // `getUploadLimitBytes('h5p')` therefore returned NaN, which made every size
  // comparison on the generic-binary upload paths false. See `S3_CONFIG.maxFileSize`.
  h5p: uploadMbField,
  teacherCredential: uploadMbField,
  // Per authoring flow (each historically its own literal).
  h5pBackgroundImage: uploadMbField,
  activitySource: uploadMbField,
  slideTemplateImage: uploadMbField,
  excalidrawImage: uploadMbField,
  h5pEditor: uploadMbField,
  // Teacher onboarding packet uploads (signed agreement PDF, W-9, degree/cert
  // files, admin signature image, base W-9 asset).
  teacherOnboarding: uploadMbField,
});
export type UploadLimitsMb = z.infer<typeof uploadLimitsMbSchema>;
export type UploadLimitKey = keyof UploadLimitsMb;

export const upsertUploadLimitsInput = z.object({
  config: uploadLimitsMbSchema,
});

// AI image generation defaults — per-purpose aspect ratio / style / quality the
// generator panel pre-selects. Purposes mirror ImagePurpose; ratios are matched
// to where each image is displayed so the object-cover crop is minimal.
export const IMAGE_GENERATION_PURPOSES = [
  'course_thumbnail',
  'reading_illustration',
  'banner',
  'social_post',
  'achievement_card',
  'certificate_background',
  // Public blog featured/hero + inline images (tech-spec-public-blog-ai-authoring).
  'blog_featured',
] as const;
export type ImageGenerationPurpose = (typeof IMAGE_GENERATION_PURPOSES)[number];

export const imagePurposeDefaultSchema = z.object({
  aspectRatio: z.enum(IMAGE_ASPECT_RATIOS),
  stylePreset: z.enum(IMAGE_STYLE_PRESETS),
  quality: z.enum(IMAGE_QUALITIES),
  background: z.enum(IMAGE_BACKGROUNDS),
});

export const imageGenerationDefaultsSchema = z.object(
  Object.fromEntries(
    IMAGE_GENERATION_PURPOSES.map((p) => [p, imagePurposeDefaultSchema])
  ) as Record<ImageGenerationPurpose, typeof imagePurposeDefaultSchema>
);

export const upsertImageGenerationDefaultsInput = z.object({
  config: imageGenerationDefaultsSchema,
});

export type ImagePurposeDefault = z.infer<typeof imagePurposeDefaultSchema>;
export type ImageGenerationDefaults = z.infer<typeof imageGenerationDefaultsSchema>;

export type BrandingConfig = z.infer<typeof brandingConfigSchema>;
export type ExerciseDefaults = z.infer<typeof exerciseDefaultsSchema>;
export type HomepageConfig = z.infer<typeof homepageConfigSchema>;
export type HomepageWidget = z.infer<typeof homepageWidgetSchema>;
export type ReadingZoneQuizConfig = z.infer<typeof readingZoneQuizConfigSchema>;
